// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { useLocation, useMatches, useNavigate } from 'react-router-dom';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Collapse,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { ModuleRoute } from '../../routes/types';
import { NotificationBadge } from '../NotificationBadge';

interface SidebarItemProps {
  notificationCount?: number;
  enableIndent?: boolean;
  drawerOpen: boolean;
  entry: ModuleRoute;
}

export const SidebarItem: React.FC<SidebarItemProps> = ({
  drawerOpen,
  notificationCount,
  entry,
  enableIndent,
}) => {
  const { divider, label, icon, children, path, getNotificationHook } = entry;
  const count = getNotificationHook?.();
  const hasNewItem = !!count;
  const nav = useNavigate();
  const matches = useMatches();
  const { pathname } = useLocation();

  const isWalletSubRoute = matches
    .filter((match) => !!match.params.walletId)
    .some((match) => path?.includes(match.params.walletId || '<never-a-match>'));

  const isSelected = children?.length ? false : pathname.includes(path || '');
  const isExpanded = !drawerOpen ? false : isWalletSubRoute;

  const expandMoreLess = isExpanded ? <ExpandLess /> : <ExpandMore />;

  const onClick = () => {
    if (path) {
      nav(path);
    }
  };

  return (
    <Box>
      <ListItem disablePadding>
        <ListItemButton
          selected={isSelected}
          sx={{ pl: enableIndent ? 9 : null }}
          onClick={onClick}
        >
          {icon && <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>}
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Typography
                  sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                >
                  {label}
                </Typography>
                {!!notificationCount && (
                  <Box sx={{ ml: 1 }}>
                    <NotificationBadge value={notificationCount} />
                  </Box>
                )}
                {hasNewItem && (
                  <Box sx={{ ml: 1 }}>
                    <NotificationBadge value="new" />
                  </Box>
                )}
              </Box>
            }
          />

          {children?.length ? expandMoreLess : null}
        </ListItemButton>
      </ListItem>
      {!!children?.length && (
        <Collapse in={isExpanded}>
          {children.map((child) => (
            <SidebarItem
              enableIndent
              drawerOpen={drawerOpen}
              key={child.label + label}
              entry={child}
            />
          ))}
        </Collapse>
      )}
      {divider && <Divider />}
    </Box>
  );
};
